/**
 * Цены и паллетоместа позиции каталога допродаж — общие функции окна дозагрузки.
 *
 * Вынесены из GazUpsellModal без изменения поведения, чтобы теми же правилами
 * пользовались и подсчёт выбранного, и строка карточки машины, и тесты (компонент
 * JSX, загрузчиком node он не разбирается).
 *
 * Своей цены и своих норм здесь не появляется: значения читаются из строки
 * каталога ровно как раньше.
 */

const parsePositive = (value) => {
  const parsed = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Штук на поддоне у позиции каталога. */
export const unitsPerPalletOf = (item) => parsePositive(
  item?.unitsPerPallet ?? item?.units_per_pallet ?? item?.UNITS_PER_PALLET
);

/**
 * Объём поддона в м³ («Поддон в ед»). Для U-блоков это НЕ количество штук —
 * подставлять его как штуки нельзя.
 */
export const palletVolumeOf = (item) => parsePositive(item?.palletUnits ?? item?.pallet_units);

/**
 * Цена позиции из строки каталога.
 *
 * Цены приходят строками ровно в том виде, в каком лежат в номенклатуре: с
 * запятой в дробной части и пробелом между разрядами («1 250,00»). Такую строку
 * `parseInt` обрывает на пробеле и превращает в 1, а пустую — в NaN, которое
 * дальше становится нулём. Поэтому здесь применяется та же нормализация, что и в
 * остальном проекте: запятая в точку, пробелы убрать (включая неразрывный).
 * Своей цены не выдумывается и не подставляется — только читается источник.
 */
export const parsePriceValue = (value) => {
  const normalized = String(value ?? '')
    .replace(',', '.')
    .replace(/[\s\u00A0]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Цена пригодна для добавления в заказ. Ноль, пустое значение и мусор ценой не
 * считаются: у зимнего клея цены нет вовсе, и отдать товар «по 0 ₽» нельзя.
 * Позиция остаётся невыбираемой, пока настоящую цену не принесёт источник.
 */
export const hasUsablePrice = (value) => {
  const parsed = parsePriceValue(value);
  return parsed !== null && parsed > 0;
};

/** Сколько паллетомест займёт количество. null — норма поддона не заведена. */
export const palletsNeededFor = (item, quantity) => {
  const qty = Number(String(quantity ?? '').replace(',', '.'));
  if (!Number.isFinite(qty) || qty <= 0) {
    return 0;
  }
  const perPallet = unitsPerPalletOf(item);
  if (perPallet) {
    return Math.ceil(qty / perPallet);
  }
  const palletVolume = palletVolumeOf(item);
  if (palletVolume && String(item?.unit || '').trim().toLowerCase() === 'м3') {
    return Math.ceil(qty / palletVolume);
  }
  return null;
};

/**
 * Наличие позиции. Складских остатков и сроков в каталоге
 * (aerated_concrete_and_brick) нет — есть только подтверждённое правило завода
 * для U-блоков. Ничего сверх него не выдумывается: у остальных категорий
 * наличие не показывается вовсе.
 */
export const isUblokItem = (item) => String(item?.category || '')
  .trim()
  .toLowerCase()
  .startsWith('u-блок');

// status и terms показывает окно дополнения: остатков в каталоге нет, поэтому
// наличие не утверждается ни в ту, ни в другую сторону — менеджер уточняет его у
// завода, а срок назван на случай, если позиции не окажется. Само правило и его
// данные прежние. short — подпись уже добавленной позиции в карточке машины, её
// формулировка не менялась.
export const GAZ_UPSELL_BACKORDER = {
  status: 'НАЛИЧИЕ УТОЧНЯЕТСЯ',
  terms: 'При отсутствии — 15–20 дней по предоплате',
  short: 'Под заказ · 15–20 дней',
};

/** Готовое наличие позиции либо null, если данных нет. */
export const getUpsellStock = (item) => (isUblokItem(item) ? GAZ_UPSELL_BACKORDER : null);
